import { UserReportResponse } from '@zond/api-interfaces';
import { Card } from '@zond/ui';
import { useState } from 'react';
import { AverageAttendanceChart } from '../../../dashboard/components/attendance/AverageAttendanceChart';
import { UserAttendanceChart } from '../../../dashboard/components/attendance/UserAttendanceChart';
import { AverageSeriesData, UserSeriesData } from '../../../dashboard/hooks/useAttendanceData';
import { useReportAttendanceData } from '../../hooks/useReportAttendanceData';
import { ReportAttendanceChartSkeleton } from './ReportAttendanceChartSkeleton';
import { ReportAttendanceControls } from './ReportAttendanceControls';
import { ReportAttendanceEmpty } from './ReportAttendanceEmpty';

/**
 * Parameters for the attendance chart component
 */
interface ReportAttendanceChartProps {
  isLoading?: boolean;
  data?: UserReportResponse;
}

/**
 * Component for displaying attendance chart in reports
 * Uses the same visual components as the dashboard
 */
export function ReportAttendanceChart({
  isLoading: externalLoading = false,
  data,
}: ReportAttendanceChartProps) {
  const [showAverageOnly, setShowAverageOnly] = useState<boolean>(true);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);

  // Get data through the adapter from useReportAttendanceData hook
  const {
    apiData,
    chartData,
    formatXAxisDate,
    isLoading: dataLoading,
  } = useReportAttendanceData(data, selectedUsers, showAverageOnly, externalLoading);

  // Handler for chart display mode change
  const handleModeChange = (showAverage: boolean) => {
    setShowAverageOnly(showAverage);
  };

  // Handler for user selection
  const handleUserSelectionChange = (userId: number) => {
    setSelectedUsers((prevSelected) => {
      // If user is already selected, remove them
      if (prevSelected.includes(userId)) {
        return prevSelected.filter((id) => id !== userId);
      }
      // Otherwise add them
      return [...prevSelected, userId];
    });
  };

  // If data is loading or missing, show skeleton
  if (externalLoading || dataLoading || !apiData) {
    return <ReportAttendanceChartSkeleton />;
  }

  // Check if there's at least one record with non-zero hours worked
  const hasActiveRecords = apiData.records.some((record) => record.hoursWorked > 0);

  // If all records have zero hours worked, show empty state
  if (!hasActiveRecords) {
    return <ReportAttendanceEmpty />;
  }

  // If chart data is empty (special case when useAttendanceData returns empty result)
  if (chartData.length === 0) {
    return <ReportAttendanceEmpty />;
  }

  return (
    <Card className="p-6 flex flex-col justify-between">
      <ReportAttendanceControls showAverageOnly={showAverageOnly} onModeChange={handleModeChange} />

      <div style={{ height: 250 }}>
        {showAverageOnly ? (
          // Average values chart
          <AverageAttendanceChart
            data={chartData as AverageSeriesData[]}
            formatXAxisDate={formatXAxisDate}
            selectedPeriod={apiData.periodType}
          />
        ) : (
          // Chart for individual users with built-in legend
          <UserAttendanceChart
            data={chartData as UserSeriesData[]}
            formatXAxisDate={formatXAxisDate}
            apiData={apiData}
            selectedUsers={selectedUsers}
            onUserSelectionChange={handleUserSelectionChange}
            selectedPeriod={apiData.periodType}
          />
        )}
      </div>
    </Card>
  );
}
